import React, {FC, useState, useEffect} from "react"
import PropTypes from "prop-types"

import {Card} from "@irati/ui/src/Layout"
import {useTheme} from "@irati/ui/src/global/useTheme"
import Loading from "@irati/ui/src/Loading"
import adminReportingService from "@irati/adminReporting/services/adminReporting"
import authentication from "@irati/auth"
import {StoryPersona, StoryHighlights, PaymentCurrencyType, StoryCards} from "@irati/types"
import usersService from "@irati/identity/services/users"
import adminTracking, {RenewalPlanSource} from "@irati-tracking/admin/admin"
import History from "@irati/utils/history"

import Welcome from "./Welcome"
import SavingsEngagement from "./SavingsEngagement"
import TopRedemptions from "./TopRedemptions"
import Persona from "./Persona"

interface Props {
  source: RenewalPlanSource
  redirectTo?: string
}

interface Context {
  apps: {[key: string]: string}
}

const Wrapped: FC<Props> = ({source, redirectTo}, {apps}: Context) => {
  const [load, setLoad] = useState<Promise<any> | null>(null)
  const [persona, setPersona] = useState<StoryPersona>()
  const [redemptions, setRedemptions] = useState<StoryCards[]>()
  const [savings, setSavings] = useState<StoryHighlights[]>()
  const [currency, setCurrency] = useState(PaymentCurrencyType.Gbp)

  const theme = useTheme()

  const trackEvent = (action?: RenewalPlanSource) => {
    adminTracking.yourPerkboxStory({
      source,
      action,
      product: RenewalPlanSource.perkboxStory
    })
  }

  const handleOnClick = () => {
    History.push({pathname: `/billing/${redirectTo}`, state: {source: RenewalPlanSource.perkboxStory}}, apps.ADMIN)
    trackEvent(RenewalPlanSource.reviewRenewalPlan)
  }

  useEffect(() => {
    const getBusinessStory = adminReportingService.getBusinessStory({businessId: authentication.business().uuid})
    const getUserRegionId = usersService.regionIdForUser(authentication.user().uuid!)
    const loaded = Promise.all([getBusinessStory, getUserRegionId]).then(([story, {regionId}]) => {
      if (story?.persona) {
        setPersona(story?.persona)
      }
      if (story?.cards) {
        setRedemptions(story?.cards)
      }
      if (story?.highlights) {
        setSavings(story?.highlights)
      }
      if (regionId === "AU" || regionId === "NZ") {
        setCurrency(PaymentCurrencyType.Aud)
      }
    })
    setLoad(loaded)
    trackEvent()
  }, [])

  return (
    <Loading load={load}>
      <Card bgColor={theme.colors.primary2}>
        <Welcome onClick={handleOnClick} />
        {savings && (
          <SavingsEngagement
            currencyCode={currency}
            engagedUsers={savings.find((item) => item.engagement !== null)?.engagement}
            savings={savings.find((item) => item.savings !== null)?.savings}
          />
        )}
        {redemptions && (
          <TopRedemptions
            deals={redemptions?.map((d) => (d.deal !== null ? d.deal : undefined)).filter((d) => d !== undefined)}
            total={redemptions?.find((d) => d.total !== null)?.total}
            currencyCode={currency}
          />
        )}
        {persona && <Persona data={persona} currencyCode={currency} onClick={handleOnClick} />}
      </Card>
    </Loading>
  )
}

Wrapped.contextTypes = {
  apps: PropTypes.object.isRequired
}

export default Wrapped
